import { useMemo } from "react";
import { Link, useParams } from "react-router-dom";
import useSeo from "@/hooks/useSeo";
import useFavorites from "@/hooks/useFavorites";
import { actors } from "@/mocks/actors";
import { movies } from "@/mocks/movies";
import { newsList } from "@/mocks/news";
import Breadcrumb from "@/components/base/Breadcrumb";
import MovieCard from "@/components/base/MovieCard";

export default function ActorDetail() {
  const { id = "" } = useParams();
  const actor = actors.find((a) => a.id === id);
  const { isActorFav, toggleActor } = useFavorites();

  const works = useMemo(
    () => (actor ? movies.filter((m) => actor.works.includes(m.id)) : []),
    [actor],
  );

  const relatedNews = useMemo(() => {
    if (!actor) return [];
    const ids = new Set(actor.works);
    return newsList.filter((n) => ids.has(n.movieId)).slice(0, 4);
  }, [actor]);

  useSeo({
    title: actor
      ? `${actor.name}演员资料与代表作品 - 我的老婆姐姐`
      : "演员详情 - 我的老婆姐姐",
    description: actor
      ? `${actor.name}个人简介、基本资料与代表作品列表。${actor.bio.slice(0, 60)}`
      : undefined,
    keywords: actor
      ? `${actor.name},${actor.name}作品,演员资料,代表作品,我的老婆姐姐`
      : undefined,
  });

  if (!actor) {
    return (
      <div className="w-full px-4 md:px-8 py-20 flex flex-col items-center text-center">
        <span className="w-16 h-16 flex items-center justify-center rounded-full bg-background-200 text-foreground-400 text-3xl mb-5">
          <i className="ri-user-line"></i>
        </span>
        <h1 className="font-heading font-bold text-xl text-foreground-950">未找到该演员</h1>
        <p className="mt-2 text-sm text-foreground-500">他/她的资料可能尚未收录</p>
        <Link
          to="/actors"
          className="mt-6 px-5 py-2.5 rounded-md bg-primary-500 text-background-50 text-sm font-label hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
        >
          返回演员库
        </Link>
      </div>
    );
  }

  const fav = isActorFav(actor.id);
  const infoRows = [
    { label: "性别", value: actor.gender },
    { label: "出生日期", value: actor.birthday },
    { label: "出生地", value: actor.birthplace },
    { label: "身高", value: actor.height },
  ];

  return (
    <div className="w-full px-4 md:px-8 py-6 md:py-8">
      <Breadcrumb items={[{ label: "演员", to: "/actors" }, { label: actor.name }]} />

      <div className="mt-5 grid grid-cols-1 md:grid-cols-[240px_1fr] gap-6 md:gap-8">
        <div className="w-full max-w-[240px] mx-auto md:mx-0">
          <div className="w-full aspect-square rounded-lg overflow-hidden bg-background-200 border border-background-200">
            <img
              src={actor.avatar}
              alt={`${actor.name} 头像`}
              title={`${actor.name} 演员资料`}
              className="w-full h-full object-cover object-top"
            />
          </div>
          <button
            type="button"
            onClick={() => toggleActor(actor.id)}
            className={`mt-4 w-full flex items-center justify-center gap-2 px-5 py-2.5 rounded-md text-sm font-label transition-colors cursor-pointer whitespace-nowrap ${
              fav
                ? "bg-accent-500 text-secondary-950 font-semibold"
                : "border border-background-300 text-foreground-800 hover:bg-background-100"
            }`}
          >
            <i className={fav ? "ri-heart-3-fill" : "ri-heart-3-line"}></i>
            {fav ? "已收藏" : "收藏演员"}
          </button>
        </div>

        <div className="min-w-0">
          <h1 className="font-heading font-extrabold text-2xl md:text-4xl text-foreground-950">
            {actor.name}
          </h1>
          <div className="mt-3 flex flex-wrap gap-2">
            {actor.tags.map((t) => (
              <span
                key={t}
                className="px-2.5 py-1 rounded-full bg-accent-100 text-accent-800 text-xs"
              >
                {t}
              </span>
            ))}
          </div>

          <dl className="mt-5 grid grid-cols-2 sm:grid-cols-4 gap-4">
            {infoRows.map((row) => (
              <div key={row.label} className="bg-background-100 rounded-lg px-3 py-3">
                <dt className="text-xs text-foreground-400">{row.label}</dt>
                <dd className="mt-1 text-sm text-foreground-900 font-medium truncate">
                  {row.value}
                </dd>
              </div>
            ))}
          </dl>

          <section className="mt-6">
            <h2 className="font-heading font-bold text-lg text-foreground-950 flex items-center gap-2 mb-3">
              <span className="w-1 h-5 rounded-full bg-accent-500"></span>
              个人简介
            </h2>
            <p className="text-sm md:text-base text-foreground-700 leading-relaxed">
              {actor.bio}
            </p>
          </section>
        </div>
      </div>

      <section className="mt-10">
        <h2 className="font-heading font-bold text-xl text-foreground-950 flex items-center gap-2 mb-4">
          <span className="w-1 h-5 rounded-full bg-accent-500"></span>
          代表作品
        </h2>
        {works.length > 0 ? (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 md:gap-4">
            {works.map((m) => (
              <MovieCard key={m.id} movie={m} />
            ))}
          </div>
        ) : (
          <p className="text-sm text-foreground-500 bg-background-100 rounded-lg px-4 py-8 text-center">
            暂无收录的代表作品
          </p>
        )}
      </section>

      {relatedNews.length > 0 && (
        <section className="mt-10">
          <h2 className="font-heading font-bold text-xl text-foreground-950 flex items-center gap-2 mb-4">
            <span className="w-1 h-5 rounded-full bg-accent-500"></span>
            相关资讯
          </h2>
          <div className="bg-background-50 border border-background-200 rounded-lg divide-y divide-background-200">
            {relatedNews.map((n) => (
              <Link
                key={n.id}
                to={`/detail/${n.movieId}`}
                className="group flex items-center gap-4 px-4 md:px-5 py-4 hover:bg-background-100 transition-colors cursor-pointer"
              >
                <span className="shrink-0 text-xs font-label text-foreground-400 w-24">
                  {n.date}
                </span>
                <span className="flex-1 text-sm md:text-base text-foreground-900 group-hover:text-primary-600 transition-colors truncate">
                  {n.title}
                </span>
                <i className="hidden sm:block ri-arrow-right-s-line text-xl text-foreground-300 group-hover:text-primary-500 transition-colors"></i>
              </Link>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}